import { createReducer, type Reducer } from "@reduxjs/toolkit";

import type {
  AddResponseAction,
  RemoveResponseAction,
  ResponsesByIdState,
  UpdateResponseAction,
} from "../../../schemas/index.ts";
import { InvalidActionError } from "../../InvalidActionError.ts";
import { addResponseAction, removeResponseAction, updateResponseAction } from "./triggerActions.ts";
import type { TriggersReducerOptions } from "./triggersReducerOptions.ts";

export type ResponsesAction = AddResponseAction | UpdateResponseAction | RemoveResponseAction;

export const responsesByIdReducer = ({ notifyEvent }: TriggersReducerOptions = {}): Reducer<
  ResponsesByIdState,
  ResponsesAction
> =>
  createReducer({} as ResponsesByIdState, (builder) => {
    builder.addCase(addResponseAction, (state, action) => {
      const { id: responseId, label } = action.payload;
      if (responseId in state) {
        throw new InvalidActionError(action, `Response already exists with id ${responseId}`);
      }

      if (notifyEvent) {
        notifyEvent({ type: "RESPONSE_ADDED", id: responseId, label });
      }

      state[responseId] = {
        label,
      };
    });

    builder.addCase(removeResponseAction, (state, action) => {
      const { id: responseId } = action.payload;

      if (notifyEvent) {
        const response = state[responseId];
        if (response) {
          const { label } = response;
          notifyEvent({ type: "RESPONSE_REMOVED", id: responseId, label });
        }
      }

      delete state[responseId];
    });

    builder.addCase(updateResponseAction, (state, action) => {
      const { id: responseId, label } = action.payload;
      if (!(responseId in state)) {
        throw new InvalidActionError(action, `Response not found with id ${responseId}`);
      }
      state[responseId].label = label;
    });
  });
